<template>
  <section
    id="componentToastBox"
    v-trap-focus
    class="toastbox"
    role="dialog"
    aria-modal="true"
    :aria-label="t('toast.ariaLabel')"
    @mouseover="pauseAutoClose"
    @mouseleave="resetAutoClose"
  >
    <button
      v-if="closeAction"
      ref="toastClose"
      class="toastbox__close-button square-icon-button"
      aria-label="Close"
      @click="close(CloseSource.CloseButton)"
    >
      <span class="material-icons" aria-hidden="true">close</span>
    </button>
    <figure class="toastbox__icon">
      <span
        :class="useSymbol ? 'material-symbols-outlined' : 'material-icons'"
        aria-hidden="true"
        >{{ icon }}</span
      >
    </figure>
    <p class="toastbox__text">
      <slot></slot>
    </p>
  </section>
</template>

<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from "vue";
import { useFocus, useTimeoutFn } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import { trapFocus } from "@/helpers/trapFocus";
import { useWpsClient } from "@/wps/client";

import { useUILayers, CloseSource } from "../UILayers";

const { close } = useUILayers();
const { t } = useI18n();

const wpsClient = useWpsClient();

const props = withDefaults(
  defineProps<{
    useSymbol?: boolean;
    closeAction?: boolean;
    timer: number;
    icon: string;
  }>(),
  {
    useSymbol: false,
    closeAction: false,
  }
);

const toastClose = useTemplateRef<HTMLButtonElement | null>("toastClose");
useFocus(toastClose, { initialValue: true });

const { start: resetAutoClose, stop: pauseAutoClose } = useTimeoutFn(
  () => close(CloseSource.Other),
  () => props.timer
);

onBeforeUnmount(() =>
  document
    .getElementById(wpsClient.media.isPDF ? "pageContainer" : "content")
    ?.focus()
);

const vTrapFocus = {
  mounted: () => {
    document.addEventListener("keydown", keyBoardEvent);
    const anchorFocus = document.getElementById("componentToastBox");
    if (anchorFocus && !props.closeAction) anchorFocus.focus();
  },
  beforeUnmount: () => {
    document.removeEventListener("keydown", keyBoardEvent);
  },
};

function keyBoardEvent(event: KeyboardEvent) {
  const isTabPressed = event.key === "Tab";
  const modal = document.getElementById("componentToastBox");

  if (!isTabPressed || !modal) return;

  trapFocus(modal, event);
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/variables.scss" as *;

.vertical-mode {
  .toastbox {
    bottom: calc($barHeight);
    right: calc($spacingM + $barHeight);
    max-width: min(350px, calc(100% - 1.5 * $barHeight));
  }
}
.toastbox {
  position: absolute;
  display: flex;
  align-items: center;
  bottom: calc($spacingL + $barHeight);
  right: calc($spacingL);
  min-width: min(280px, calc(100% - 2 * $barHeight));
  max-width: min(350px, calc(100% - $spacingXL));
  min-height: 50px;
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  box-shadow: 0 4px 23px rgba(0, 0, 0, 0.09);
  border: 3px solid rgba(158, 158, 158, 0.2);
  border-radius: $spacingM;
  padding: $spacingM $spacingXL $spacingM $spacingM;
  z-index: 5;
  @media (max-width: $screenSM) {
    bottom: calc($spacingL + (2 * $barHeight));
  }
  &__icon {
    margin-right: $spacingM;
  }
  &__text {
    margin: 0;
    font-size: 0.9rem;
    a {
      text-decoration: underline;
    }
  }
  &__close-button {
    position: absolute;
    color: var(--DM__theme-font-color);

    &.square-icon-button {
      width: 24px;
      height: 24px;
      top: $spacingS;
      right: $spacingM;
    }
    .material-icons {
      font-size: 0.9rem;
    }
  }
}
</style>
